import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Slide08ZPD: React.FC = () => {
  const [difficulty, setDifficulty] = useState(50);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDifficulty(prev => {
        if (prev >= 75) setDirection(-1);
        if (prev <= 25) setDirection(1);
        return prev + direction * 5;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [direction]);

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-4 text-center"
      >
        Zone of Proximal Development
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-2xl text-[#94a3b8] mb-12 text-center max-w-4xl"
      >
        AI keeps students in the "sweet spot" — challenging enough to grow, but not so hard they give up
      </motion.p>

      {/* ZPD Visualization */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-4xl"
      >
        {/* Three zones */}
        <div className="flex items-stretch gap-4 mb-8">
          {/* Too Easy */}
          <div
            className="flex-1 rounded-xl p-6 text-center"
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '2px solid rgba(239, 68, 68, 0.4)',
            }}
          >
            <div className="text-4xl mb-3">😴</div>
            <div className="text-xl font-semibold text-[#ef4444] mb-2">Too Easy</div>
            <div className="text-lg text-[#94a3b8]">Boredom</div>
            <div className="text-sm text-[#64748b] mt-2">No growth, disengagement</div>
          </div>

          {/* ZPD - Sweet Spot */}
          <div
            className="flex-1 rounded-xl p-6 text-center relative overflow-hidden"
            style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '3px solid #10b981',
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)',
            }}
          >
            <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-[#10b981] text-white">
              TARGET
            </div>
            <div className="text-4xl mb-3">🎯</div>
            <div className="text-xl font-semibold text-[#10b981] mb-2">Zone of Proximal Development</div>
            <div className="text-lg text-white">Optimal Challenge</div>
            <div className="text-sm text-[#94a3b8] mt-2">Maximum learning & engagement</div>
          </div>

          {/* Too Hard */}
          <div
            className="flex-1 rounded-xl p-6 text-center"
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '2px solid rgba(239, 68, 68, 0.4)',
            }}
          >
            <div className="text-4xl mb-3">😰</div>
            <div className="text-xl font-semibold text-[#ef4444] mb-2">Too Hard</div>
            <div className="text-lg text-[#94a3b8]">Frustration</div>
            <div className="text-sm text-[#64748b] mt-2">Anxiety, giving up</div>
          </div>
        </div>

        {/* Difficulty slider visualization */}
        <div
          className="rounded-xl p-6"
          style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-xl text-white font-medium">AI Difficulty Adjustment</span>
            <span className="text-lg text-[#10b981] font-semibold">Real-time</span>
          </div>

          {/* Slider track */}
          <div className="relative h-8 rounded-full overflow-hidden mb-4" style={{ background: 'rgba(255,255,255,0.1)' }}>
            {/* Zones background */}
            <div className="absolute inset-0 flex">
              <div className="w-1/4 bg-[#ef4444]/20" />
              <div className="w-1/2 bg-[#10b981]/30" />
              <div className="w-1/4 bg-[#ef4444]/20" />
            </div>

            {/* Moving indicator */}
            <motion.div
              className="absolute top-1 bottom-1 w-6 rounded-full bg-white shadow-lg"
              style={{ left: `calc(${difficulty}% - 12px)` }}
            />
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-[#ef4444]">Too Easy</span>
            <span className="text-[#10b981] font-semibold">Sweet Spot</span>
            <span className="text-[#ef4444]">Too Hard</span>
          </div>
        </div>

        {/* How it works */}
        <div className="flex justify-center gap-12 mt-8">
          <div className="text-center">
            <div className="text-2xl mb-2">📈</div>
            <div className="text-lg text-white">Doing well?</div>
            <div className="text-[#94a3b8]">Increase difficulty</div>
          </div>
          <div className="text-center">
            <div className="text-2xl mb-2">📉</div>
            <div className="text-lg text-white">Struggling?</div>
            <div className="text-[#94a3b8]">Provide hints & easier questions</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Slide08ZPD;
